import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import HairDisclaimerModal from '../components/HairDisclaimerModal';
import { useHairSession } from '../context/HairSessionContext';
import { captureDeviceInfo } from '../utils/deviceCapture';
import { hairAppPath, hairLastCustomerStorageKey, HAIR_DISCLAIMER_KEY } from '../config/hairApp';
import { clearSession, createNewSessionWithBackend } from '../utils/sessionManager';
import {
  createSalonSessionFromFace,
  resolveGuestUserDataForAnalysis,
} from '../../../../enterprise/salon/utils/bootstrapFaceSalonSession';
import { resolveExperienceHubPath } from '../../../../enterprise/salon/utils/salonHomeNav';
import '../styles/PageBackground.css';
import './LandingPage.css';

/**
 * Hair Analyze entry: skip marketing landing — open disclaimer, then continue to capture.
 */
const LandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token: tokenFromParams } = useParams();
  const { token: sessionToken, setToken } = useHairSession();
  const token = tokenFromParams || sessionToken;
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [disclaimerOpen, setDisclaimerOpen] = useState(true);

  useEffect(() => {
    if (tokenFromParams) setToken(tokenFromParams);
  }, [tokenFromParams, setToken]);

  useEffect(() => {
    setDisclaimerOpen(true);
  }, []);

  const leaveFlow = () => {
    if (isButtonLoading) return;
    setDisclaimerOpen(false);
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(resolveExperienceHubPath('/enterprise/salon/hub'), { replace: true });
  };

  const handleStartAfterConfirm = async () => {
    const deviceInfo = captureDeviceInfo();
    const API_BASE_URL = process.env.REACT_APP_API_URL || '';
    const scanMode = 'advanced';
    setIsButtonLoading(true);
    try {
      clearSession();
      if (token) {
        localStorage.removeItem(hairLastCustomerStorageKey(token));
      }
    } catch (_) {}

    const userData = resolveGuestUserDataForAnalysis(location.state);
    let sessionIdForState = null;
    try {
      // Store against enterprise salon session so customer fields persist in DB.
      const created = await createSalonSessionFromFace({
        token: token || null,
        deviceInfo,
        locationState: {
          ...(location.state || {}),
          faceUserId: userData.faceUserId || location.state?.faceUserId,
          customerName: userData.name,
          mobile: userData.mobileNumber,
          email: userData.email,
          gender: userData.gender,
          age: userData.age,
          dob: userData.dob,
          faceProfile: location.state?.faceProfile,
        },
      });
      sessionIdForState = created?.sessionId || null;
    } catch (e) {
      console.warn('Salon hair session create failed, falling back', e);
      try {
        if (API_BASE_URL) {
          sessionIdForState = await createNewSessionWithBackend(API_BASE_URL, {
            app_session_token: token || undefined,
            device_info: deviceInfo,
          });
        }
      } catch (err) {
        console.warn('Hair session create failed', err);
      }
    }

    try {
      sessionStorage.setItem(HAIR_DISCLAIMER_KEY, '1');
    } catch (_) {}

    const navState = {
      ...(location.state || {}),
      ...(token ? { token } : {}),
      deviceInfo,
      deferSession: false,
      disclaimerAccepted: true,
      scanMode,
      userData,
      customerName: userData.name,
      faceUserId: userData.faceUserId || undefined,
      ...(sessionIdForState ? { sessionId: sessionIdForState } : {}),
    };

    try {
      navigate(hairAppPath('/insight/capture'), { replace: true, state: navState });
    } finally {
      setIsButtonLoading(false);
      setDisclaimerOpen(false);
    }
  };

  return (
    <div className="landing-page-shell page-bg hair-landing-enriched hair-landing-disclaimer-only" id="landing-page-shell">
      <HairDisclaimerModal
        isOpen={disclaimerOpen}
        onConfirm={() => {
          void handleStartAfterConfirm();
        }}
        onCancel={leaveFlow}
        confirming={isButtonLoading}
      />
    </div>
  );
};

export default LandingPage;
